import { createContext, useContext, useState, useCallback } from "react";
import { LifeBuoy, Phone, MessageSquare, PhoneCall, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteContent } from "@/hooks/useSiteContent";

const HelpContext = createContext(null);
export const useHelp = () => useContext(HelpContext) || { open: () => {} };

export const HelpProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  return (
    <HelpContext.Provider value={{ isOpen, open, close, setIsOpen }}>
      {children}
    </HelpContext.Provider>
  );
};

const initialForm = { name: "", phone: "", email: "", message: "" };

export const HelpWidget = () => {
  const { t, lang } = useLanguage();
  const { content } = useSiteContent();
  const { isOpen, setIsOpen, open } = useHelp();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const phone = content?.contact_phone || "+91 98100 11223";

  const submit = async (type) => {
    if (!form.name || !form.phone) {
      toast.error(lang === "hi" ? "कृपया नाम और फ़ोन नंबर भरें" : "Please enter your name and phone number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/callback-requests", {
        ...form,
        request_type: type,
        preferred_language: lang,
      });
      toast.success(t("callback_success"));
      setForm(initialForm);
      setIsOpen(false);
    } catch (e) {
      toast.error(lang === "hi" ? "कुछ गलत हुआ, फिर कोशिश करें" : "Something went wrong, please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <>
      {/* Floating button (bottom-left to avoid the bottom-right badge) */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        onClick={open}
        data-testid="help-widget-fab"
        className="fixed bottom-5 left-5 z-50 flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95 sm:hidden"
      >
        <LifeBuoy className="h-5 w-5" />
        {t("get_help")}
      </motion.button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{t("help_title")}</DialogTitle>
            <DialogDescription>{t("help_sub")}</DialogDescription>
          </DialogHeader>

          <a href={`tel:${phone.replace(/\s/g, "")}`} data-testid="help-call-now" className="flex items-center justify-between rounded-lg border bg-accent/40 px-4 py-3 transition-colors hover:bg-accent">
            <span className="flex items-center gap-2 font-semibold text-accent-foreground"><PhoneCall className="h-5 w-5" /> {t("call_now")}</span>
            <span className="font-mono text-sm">{phone}</span>
          </a>

          <Tabs defaultValue="callback" className="mt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="callback" data-testid="help-tab-callback"><Phone className="mr-1 h-4 w-4" />{t("request_callback")}</TabsTrigger>
              <TabsTrigger value="message" data-testid="help-tab-message"><MessageSquare className="mr-1 h-4 w-4" />{t("leave_message")}</TabsTrigger>
            </TabsList>

            <div className="mt-4 space-y-3">
              <div>
                <Label htmlFor="help-name">{t("your_name")}</Label>
                <Input id="help-name" name="name" value={form.name} onChange={onChange} data-testid="help-name-input" />
              </div>
              <div>
                <Label htmlFor="help-phone">{t("your_phone")}</Label>
                <Input id="help-phone" name="phone" value={form.phone} onChange={onChange} data-testid="help-phone-input" />
              </div>
              <TabsContent value="callback" className="m-0">
                <Button className="w-full" disabled={submitting} onClick={() => submit("callback")} data-testid="help-submit-callback">
                  {submitting ? "..." : t("request_callback")}
                </Button>
              </TabsContent>
              <TabsContent value="message" className="m-0 space-y-3">
                <div>
                  <Label htmlFor="help-message">{t("message_label")}</Label>
                  <Textarea id="help-message" name="message" rows={3} value={form.message} onChange={onChange} data-testid="help-message-input" />
                </div>
                <Button className="w-full" disabled={submitting} onClick={() => submit("message")} data-testid="help-submit-message">
                  {submitting ? "..." : t("send")}
                </Button>
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};
